import {
    ClickAwayListener,
    Dialog,
    DialogTitle,
    DialogContent,
    Link,
    Tooltip,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { X, FlaskConical, Info, Divide, Minus } from 'lucide-react';
import { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
    resultsBox: {
        padding: 'var(--space-default) var(--space-xxl)',
        display: 'flex',
        flexDirection: 'column',
        gap: 30
    },
    resultsEquation: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 'var(--space-xl)'
    },
    divisionBar: {
        margin: 'var(--space-s)',
        backgroundColor: 'var(--color-text-muted)',
        width: 'calc(var(--space-xxl) * 3)',
        height: '1px'
    },
    resultsTitle: {
        flex: '0 0 22%',
        margin: 'var(--space-l) 0',
        textAlign: 'left'
    },
    flexRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center'
    },
    mobileInfo: {
        '&:hover': {
            textDecoration: 'none'
        }
    },
    secretSauceEquationsGridMobile: {
        width: '100%'
    },
    multiplyHorizontal: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center'
    },
    metalBackground: {
        backgroundImage: 'linear-gradient(180deg, var(--neutral-1) 0%,rgba(0,0,0,0.75) 100%), url(https://foundrydigital.com/wp-content/uploads/Dot-Pattern-gradient-e1699561060462.png)!important',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        backgroundPosition: '0 10%',
        overflowX: 'hidden',
        padding: '40px'
    }
}));

const CalculationBreakdownDialog = ({ open, onClose }) => {
    const { classes, cx } = useStyles();
    const theme = useTheme();
    const mediumDevice = useMediaQuery(theme.breakpoints.down('md'));

    const [twoHover, setTwoHover] = useState(false);
    const [secondsPerDayHover, setSecondsPerDayHover] = useState(false);
    const [networkHover, setNetworkHover] = useState(false);
    const [subsidyHover, setSubsidyHover] = useState(false);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            slotProps={{
                paper: {
                    style: {
                        backgroundColor: 'var(--neutral-2)',
                        color: 'var(--color-text)',
                    }
                }
            }}
        >
            <DialogTitle style={{ borderBottom: '1px solid var(--color-border)' }}>
                <p className="m-heading is-size-xl" style={{ color: 'var(--core-white)' }}>Calculation Breakdown</p>
                <div
                    style={
                        mediumDevice
                            ? { position: 'absolute', top: '12px', right: '12px', cursor: 'pointer' }
                            : { position: 'absolute', top: '15px', right: '36px', cursor: 'pointer' }
                    }
                    onClick={onClose}
                >
                    <X />
                </div>
            </DialogTitle>
            <DialogContent>
                <div style={{ padding: theme.spacing(2.0) }}>
                    <div className={classes.metalBackground}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', justifyContent: "flex-start", alignItems: "center", position: "relative", paddingBottom: theme.spacing(1) }}>
                            <p className="m-heading is-size-xl is-accent" style={{ marginRight: 'var(--space-s)' }}>Methodology</p>
                            <FlaskConical className='m-text is-accent' size={23} strokeWidth={1} />
                        </div>
                        <div className='m-box'>
                            {mediumDevice ?
                                <div className={classes.secretSauceEquationsGridMobile} style={{ display: "flex", flexDirection: 'column' }}>
                                    <p className='m-text is-size-l' style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", margin: "var(--space-xxl) 0 var(--space-s) 0" }}>Aggregate Share Difficulty: </p>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <p className='m-text is-size-l' style={{ margin: '0 1rem 0 0' }}>Hashrate</p>
                                        <div className={classes.divisionBar} />
                                        <div className={classes.multiplyHorizontal} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                            <ClickAwayListener onClickAway={() => setTwoHover(false)}>
                                                <Tooltip
                                                    title={<p className='m-text'>Zcash difficulty is often denoted as the relative difficulty with respect to the genesis block, which required approximately 2^32 hashes.</p>} // TODO Update this text
                                                    open={twoHover}
                                                    arrow
                                                    placement='top'
                                                >
                                                    <span className='two-hover m-text is-size-l' style={{ position: "relative", marginLeft: "20px", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: "4px", margin: '0 1.2rem 0 0' }} onClick={() => setTwoHover(true)} >
                                                        2
                                                        <span className='m-text' style={{ position: "absolute", marginBottom: "12px", fontSize: "10px" }}>
                                                            32
                                                        </span>
                                                    </span>
                                                </Tooltip>
                                            </ClickAwayListener>

                                            <X size={16} className='m-text is-size-l' style={{ margin: '0 .5rem 0 0' }} />
                                            <p className='seconds-per-day-hover m-text is-size-l' style={{ textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: "4px" }} onClick={() => setSecondsPerDayHover(true)} >
                                                <ClickAwayListener onClickAway={() => setSecondsPerDayHover(false)}>
                                                    <Tooltip
                                                        title={<p className='m-text'>Amount of hashes are measured in hashes per second.</p>}
                                                        open={secondsPerDayHover}
                                                        arrow
                                                        placement='top'
                                                    >
                                                        <p className='m-text is-size-l'>Seconds per day</p>
                                                    </Tooltip>
                                                </ClickAwayListener>
                                            </p>
                                        </div>
                                    </div>

                                    <p className='m-text is-size-l' style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", margin: "var(--space-xxl) 0 var(--space-s) 0" }}>PPS Base:</p>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: "0 0 var(--space-xxl) 0" }} >
                                        <p className='m-text is-size-l' style={{ margin: '0 1rem 0 0' }}>Aggregate Share Difficulty </p>
                                        <div className={classes.divisionBar} style={{ width: '250px' }} />
                                        <div className={classes.multiplyHorizontal} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                            <p className='network-hover m-text' style={{ textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: "4px" }} onClick={() => setNetworkHover(true)} >
                                                <ClickAwayListener onClickAway={() => setNetworkHover(false)}>
                                                    <Tooltip
                                                        title={<p className='m-text'>The Network Difficulty based on the selected date.</p>}
                                                        open={networkHover}
                                                        arrow
                                                        placement='top'
                                                    >
                                                        <p className='m-text is-size-l' style={{ whiteSpace: 'nowrap' }}>Network Difficulty</p>
                                                    </Tooltip>
                                                </ClickAwayListener>
                                            </p>
                                            <X size={16} className='m-text' style={{ margin: '0 var(--space-s)' }} />
                                            <p className='subsidy-hover m-text' style={{ textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: "4px" }} onClick={() => setSubsidyHover(true)} >
                                                <ClickAwayListener onClickAway={() => setSubsidyHover(false)}>
                                                    <Tooltip
                                                        title={<p className='m-text'>The current Block Subsidy in ZEC.</p>}
                                                        open={subsidyHover}
                                                        arrow
                                                        placement='top'
                                                    >
                                                        <p className='m-text is-size-l'>Block Subsidy (ZEC)</p>
                                                    </Tooltip>
                                                </ClickAwayListener>
                                            </p>
                                        </div>
                                    </div>
                                    <div className={classes.flexRow}>
                                        <Link
                                            className={classes.mobileInfo}
                                            target='_blank'
                                            href='https://pool-faq.foundrydigital.com/what-is-foundry-usa-pools-payout-methodology' // TODO update this link
                                        >
                                            <p className='m-text is-size-l' style={{ display: "flex", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: "4px", justifyContent: "flex-start", alignItems: "center", margin: "10px 2px 10px 0" }}>PPLNS Payout Amount Calculation: </p>
                                        </Link>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', justifyContent: "center", alignItems: "center" }}>
                                        <p className='m-text is-size-l'>PPS Base</p>
                                        <X size={16} style={{ margin: '0 3px' }} className='m-text' />
                                        <p className='m-text is-size-l'> PPLNS Rate</p>
                                        <X size={16} style={{ margin: '0 3px' }} className='m-text' />
                                        <p style={{ fontSize: '20px', marginRight: '.5rem' }} className='m-text is-size-l'>&#10222;</p>
                                        <p className='m-text is-size-l' style={{ whiteSpace: 'nowrap', marginRight: 'var(--space-s)' }}>1 -</p>
                                        <p className='m-text is-size-l'>Pool Fee %</p>
                                        <Divide size={16} className='m-text' style={{ margin: '0 .5rem' }} />
                                        <p className='m-text is-size-l'>100</p>
                                        <p style={{ fontSize: '20px', margin: '0 .5rem' }} className='m-text is-size-l'>&#10223;</p>
                                    </div>
                                </div>
                                :
                                <div className={classes.resultsBox}>
                                    <div className={classes.resultsEquation}>
                                        <p className={cx('m-text is-size-l', classes.resultsTitle)}>Aggregate Share Difficulty: </p>
                                        <div className={classes.flexRow}>
                                            <p className='m-text is-size-l' style={{ margin: '0 var(--space-s) 0 0' }}>Hashrate</p>
                                            <Divide size={16} className='m-text is-size-l' style={{ margin: '0 var(--space-s) 0 0' }} />
                                            <ClickAwayListener onClickAway={() => setTwoHover(false)}>
                                                <Tooltip
                                                    title={<p className='m-text'>Zcash difficulty is often denoted as the relative difficulty with respect to the genesis block, which required approximately 2^32 hashes.</p>}
                                                    open={twoHover}
                                                    arrow
                                                    placement='top'
                                                >
                                                    <span className='two-hover m-text is-size-l' style={{ position: "relative", marginLeft: "20px", textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: "4px", margin: '0 1.2rem 0 0', cursor: 'default' }} onMouseEnter={() => setTwoHover(true)} onMouseLeave={() => setTwoHover(false)}>
                                                        2
                                                        <span className='m-text' style={{ position: "absolute", marginBottom: "12px", fontSize: "10px" }}>
                                                            32
                                                        </span>
                                                    </span>
                                                </Tooltip>
                                            </ClickAwayListener>
                                            <X size={16} className='m-text is-size-l' style={{ margin: '0 var(--space-s) 0 0' }} />
                                            <p className='seconds-per-day-hover m-text is-size-l' style={{ textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: "4px", cursor: 'default' }} onMouseEnter={() => setSecondsPerDayHover(true)} onMouseLeave={() => setSecondsPerDayHover(false)}>
                                                <ClickAwayListener onClickAway={() => setSecondsPerDayHover(false)}>
                                                    <Tooltip
                                                        title={<p className='m-text'>Amount of hashes are measured in hashes per second.</p>}
                                                        open={secondsPerDayHover}
                                                        arrow
                                                        placement='top'
                                                    >
                                                        <p className='m-text is-size-l'>Seconds per day</p>
                                                    </Tooltip>
                                                </ClickAwayListener>
                                            </p>
                                        </div>
                                    </div>

                                    <div className={classes.resultsEquation}>
                                        <p className={cx('m-text is-size-l', classes.resultsTitle)}>PPS Base:</p>
                                        <div className={classes.flexRow}>
                                            <p className='m-text is-size-l' style={{ margin: '0 var(--space-s) 0 0' }}>Aggregate Share Difficulty </p>
                                            <Divide size={16} className='m-text is-size-l' style={{ margin: '0 var(--space-s) 0 0' }} />
                                            <p className='network-hover m-text is-size-l' style={{ textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: "4px", cursor: 'default' }} onMouseEnter={() => setNetworkHover(true)} onMouseLeave={() => setNetworkHover(false)}>
                                                <ClickAwayListener onClickAway={() => setNetworkHover(false)}>
                                                    <Tooltip
                                                        title={<p className='m-text'>The Network Difficulty based on the selected date.</p>}
                                                        open={networkHover}
                                                        arrow
                                                        placement='top'
                                                    >
                                                        <p className='m-text is-size-l'>Network Difficulty</p>
                                                    </Tooltip>
                                                </ClickAwayListener>
                                            </p>
                                            <X size={16} className='m-text is-size-l' style={{ margin: '0 var(--space-s) 0 var(--space-s)' }} />
                                            <p className='subsidy-hover m-text is-size-l' style={{ textDecoration: "underline", textDecorationStyle: "dotted", textUnderlineOffset: "4px", cursor: 'default' }} onMouseEnter={() => setSubsidyHover(true)} onMouseLeave={() => setSubsidyHover(false)}>
                                                <ClickAwayListener onClickAway={() => setSubsidyHover(false)}>
                                                    <Tooltip
                                                        title={<p className='m-text'>The current Block Subsidy in ZEC.</p>}
                                                        open={subsidyHover}
                                                        arrow
                                                        placement='top'
                                                    >
                                                        <p className='m-text is-size-l'>Block Subsidy (ZEC)</p>
                                                    </Tooltip>
                                                </ClickAwayListener>
                                            </p>
                                        </div>
                                    </div>
                                    <div className={classes.resultsEquation} style={{ position: 'relative' }}>
                                        <p className={cx('m-text is-size-l', classes.resultsTitle)}>PPLNS Payout Amount Calculation: </p>
                                        <div style={{ position: 'absolute', top: '1.75rem', left: '6.5rem' }}>
                                            <Link
                                                target='_blank'
                                                href='https://pool-faq.foundrydigital.com/what-is-foundry-usa-pools-payout-methodology'
                                            >
                                                <Info size={16} className='m-text' />
                                            </Link>
                                        </div>
                                        <div className={classes.flexRow}>
                                            <p className='m-text is-size-l' style={{ whiteSpace: 'nowrap' }}>PPS Base</p>
                                            <X size={16} style={{ marginLeft: "var(--space-s)" }} className='m-text is-size-l' />
                                            <p style={{ marginLeft: "var(--space-s)", whiteSpace: 'nowrap' }} className='m-text is-size-l'> PPLNS Rate</p>
                                            <X size={16} style={{ marginLeft: "var(--space-s)" }} className='m-text is-size-l' />
                                            <p style={{ fontSize: '20px', marginLeft: "var(--space-s)" }} className='m-text is-size-l'>&#10222;</p>
                                            <p style={{ marginLeft: "var(--space-s)", whiteSpace: 'nowrap' }} className='m-text is-size-l'>1</p>
                                            <Minus size={16} className='m-text' style={{ margin: '0 var(--space-s)' }} />
                                            <p style={{ marginLeft: "var(--space-s)", whiteSpace: 'nowrap' }} className='m-text is-size-l'>Pool Fee %</p>
                                            <Divide size={16} className='m-text' style={{ margin: '0 0 0 .5rem' }} />
                                            <p style={{ marginLeft: "var(--space-s)", whiteSpace: 'nowrap' }} className='m-text is-size-l'>100</p>
                                            <p style={{ fontSize: '20px', marginLeft: "var(--space-s)" }} className='m-text is-size-l'>&#10223;</p>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CalculationBreakdownDialog;
